import { useState, useEffect } from 'react';
import { Card, Badge, Button, Select, DateInput } from '@atoms/index';
import { DetailModal, AlertModal } from '@organisms/index';
import { useFinancialDashboard, useSettlements, useCommissions } from '@/hooks/useAdminFinance';
import { adminFinanceService, SettlementQuery, Settlement, Payout, PayoutStatusType, PayoutRecipientType, ReconciliationResult } from '@services/adminFinanceService';
import { formatCurrency } from '@/utils/currency';
import { cn } from '@/utils/cn';
import { TrendChart } from '@organisms/TrendChart';

export default function FinanceTab() {
  const [selectedPeriod, setSelectedPeriod] = useState<'today' | 'week' | 'month'>('month');
  const [settlementFilters, setSettlementFilters] = useState<SettlementQuery>({
    page: 1,
    limit: 20,
  });
  const [selectedSettlement, setSelectedSettlement] = useState<Settlement | null>(null);
  const [isSettlementModalOpen, setIsSettlementModalOpen] = useState(false);
  const [viewType, setViewType] = useState<'settlements' | 'pendingCash' | 'commissions' | 'payouts' | 'reconciliation'>('settlements');
  const [pendingCashPayments, setPendingCashPayments] = useState<any>(null);
  const [loadingPendingCash, setLoadingPendingCash] = useState(false);
  const [alertState, setAlertState] = useState<{ isOpen: boolean; title: string; message: string; variant: 'danger' | 'success' | 'warning' }>({ isOpen: false, title: '', message: '', variant: 'danger' });
  const [confirmState, setConfirmState] = useState<{ isOpen: boolean; title: string; message: string; onConfirm: () => void }>({ isOpen: false, title: '', message: '', onConfirm: () => {} });
  const [payouts, setPayouts] = useState<Payout[]>([]);
  const [payoutsTotal, setPayoutsTotal] = useState(0);
  const [payoutsPage, setPayoutsPage] = useState(1);
  const [payoutsLoading, setPayoutsLoading] = useState(false);
  const [payoutStatusFilter, setPayoutStatusFilter] = useState<PayoutStatusType | ''>('');
  const [payoutTypeFilter, setPayoutTypeFilter] = useState<PayoutRecipientType | ''>('');
  const [reconciliationResult, setReconciliationResult] = useState<ReconciliationResult | null>(null);
  const [reconciliationLoading, setReconciliationLoading] = useState(false);

  const dashboard = useFinancialDashboard(selectedPeriod);
  const settlements = useSettlements(settlementFilters);
  const commissions = useCommissions(
    settlementFilters.restaurantId,
    settlementFilters.startDate,
    settlementFilters.endDate,
  );

  const loadPayouts = async (page = 1) => {
    setPayoutsLoading(true);
    try {
      const data = await adminFinanceService.listPayouts({
        page,
        limit: 20,
        ...(payoutStatusFilter && { status: payoutStatusFilter }),
        ...(payoutTypeFilter && { recipientType: payoutTypeFilter }),
      });
      setPayouts(data.payouts);
      setPayoutsTotal(data.total);
      setPayoutsPage(page);
    } catch (error) {
      console.error('Failed to load payouts:', error);
      setAlertState({ isOpen: true, title: 'Hata', message: 'Hakediş verileri yüklenemedi', variant: 'danger' });
    } finally {
      setPayoutsLoading(false);
    }
  };

  useEffect(() => {
    if (viewType === 'payouts') {
      loadPayouts(1);
    }
  }, [viewType, payoutStatusFilter, payoutTypeFilter]);

  const handleProcessPayout = (payoutId: string) => {
    setConfirmState({
      isOpen: true,
      title: 'Hakediş Onayı',
      message: 'Bu hakediş ödemesini onaylamak istediğinize emin misiniz? Bu işlem geri alınamaz.',
      onConfirm: async () => {
        setConfirmState((c) => ({ ...c, isOpen: false }));
        try {
          await adminFinanceService.processPayout(payoutId);
          setAlertState({ isOpen: true, title: 'Başarılı', message: 'Hakediş işlendi', variant: 'success' });
          loadPayouts(payoutsPage);
        } catch (error) {
          console.error('Failed to process payout:', error);
          setAlertState({ isOpen: true, title: 'Hata', message: 'Hakediş işlenemedi', variant: 'danger' });
        }
      },
    });
  };

  const handleFailPayout = (payoutId: string) => {
    const reason = prompt('Başarısızlık nedenini girin:');
    if (!reason) return;
    setConfirmState({
      isOpen: true,
      title: 'Hakediş Reddi',
      message: `Bu hakediş ödemesini başarısız olarak işaretlemek istediğinize emin misiniz?\nNeden: ${reason}`,
      onConfirm: async () => {
        setConfirmState((c) => ({ ...c, isOpen: false }));
        try {
          await adminFinanceService.failPayout(payoutId, reason);
          setAlertState({ isOpen: true, title: 'Başarılı', message: 'Hakediş başarısız olarak işaretlendi', variant: 'warning' });
          loadPayouts(payoutsPage);
        } catch (error) {
          console.error('Failed to fail payout:', error);
          setAlertState({ isOpen: true, title: 'Hata', message: 'Hakediş durumu güncellenemedi', variant: 'danger' });
        }
      },
    });
  };

  const handleGeneratePayouts = async (type: 'restaurant' | 'courier') => {
    try {
      const result = type === 'restaurant'
        ? await adminFinanceService.generateRestaurantPayouts()
        : await adminFinanceService.generateCourierPayouts();
      setAlertState({
        isOpen: true,
        title: 'Başarılı',
        message: `${result.created} hakediş oluşturuldu, ${result.skipped} atlandı`,
        variant: 'success',
      });
      loadPayouts(1);
    } catch (error) {
      console.error('Failed to generate payouts:', error);
      setAlertState({ isOpen: true, title: 'Hata', message: 'Hakediş oluşturulamadı', variant: 'danger' });
    }
  };

  const handleExportCsv = async () => {
    try {
      const blob = await adminFinanceService.exportPayoutsCsv(payoutStatusFilter || undefined);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `payouts-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to export CSV:', error);
      setAlertState({ isOpen: true, title: 'Hata', message: 'CSV dışa aktarılamadı', variant: 'danger' });
    }
  };

  const handleRunReconciliation = async () => {
    setReconciliationLoading(true);
    try {
      const result = await adminFinanceService.runReconciliation();
      setReconciliationResult(result);
    } catch (error) {
      console.error('Failed to run reconciliation:', error);
      setAlertState({ isOpen: true, title: 'Hata', message: 'Mutabakat çalıştırılamadı', variant: 'danger' });
    } finally {
      setReconciliationLoading(false);
    }
  };

  const handleViewSettlement = async (settlementId: string) => {
    try {
      const settlement = await adminFinanceService.getSettlementDetails(settlementId);
      setSelectedSettlement(settlement);
      setIsSettlementModalOpen(true);
    } catch (error) {
      console.error('Failed to load settlement details:', error);
      setAlertState({ isOpen: true, title: 'Hata', message: 'Ödeme detayları yüklenemedi', variant: 'danger' });
    }
  };


  const totalPages = Math.ceil((settlements.data?.total || 0) / (settlementFilters.limit || 20));

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Finansal Yönetim</h2>
          <p className="text-gray-600">Platform finansal durumu ve ödemeler</p>
        </div>
      </div>

      {dashboard.isLoading && (
        <div className="text-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4" />
          <p className="text-gray-600">Finansal veriler yükleniyor...</p>
        </div>
      )}

      {dashboard.error && (
        <Card className="bg-warning/10 border border-warning/20">
          <p className="text-warning">{dashboard.error}</p>
        </Card>
      )}

      {!dashboard.isLoading && !dashboard.error && dashboard.data && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card>
              <div className="text-center">
                <p className="text-3xl font-bold text-primary">
                  {formatCurrency(dashboard.data.totalRevenue)}
                </p>
                <p className="text-sm text-gray-600">Toplam Gelir</p>
              </div>
            </Card>

            <Card>
              <div className="text-center">
                <p className="text-3xl font-bold text-success">
                  {formatCurrency(dashboard.data.commissionRevenue)}
                </p>
                <p className="text-sm text-gray-600">Komisyon Geliri</p>
              </div>
            </Card>

            <Card>
              <div className="text-center">
                <p className="text-3xl font-bold text-info">
                  {formatCurrency(dashboard.data.deliveryFees)}
                </p>
                <p className="text-sm text-gray-600">Kurye Ücretleri</p>
              </div>
            </Card>

            <Card>
              <div className="text-center">
                <p className="text-3xl font-bold text-warning">
                  {formatCurrency(dashboard.data.netProfit)}
                </p>
                <p className="text-sm text-gray-600">Net Kar</p>
              </div>
            </Card>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Card>
              <div className="text-center">
                <p className="text-2xl font-bold text-primary">
                  {formatCurrency(dashboard.data.totalPlatformRevenue)}
                </p>
                <p className="text-sm text-gray-600">Toplam Platform Geliri</p>
              </div>
            </Card>

            <Card className="border-warning/30 bg-warning/10">
              <div className="text-center">
                <p className="text-2xl font-bold text-warning">
                  {formatCurrency(dashboard.data.totalCashInMarket)}
                </p>
                <p className="text-sm text-gray-600">Piyasadaki Nakit (Risk)</p>
              </div>
            </Card>
          </div>

          <Card>
            <h3 className="text-lg font-bold text-gray-900 mb-4">Finansal Trend</h3>
            <TrendChart
              data={dashboard.data.trends.map((t) => ({
                date: t.date,
                dateLabel: new Date(t.date).toLocaleDateString('tr-TR'),
                orders: 0,
                revenue: t.revenue,
              }))}
              period={selectedPeriod}
            />
          </Card>
        </>
      )}

      <Card>
        <div className="mb-4">
          <h3 className="text-lg font-bold text-gray-900 mb-2">Finansal İşlemler</h3>
          <p className="text-sm text-gray-600">
            {viewType === 'settlements' &&
              'Siparişler teslim edildiğinde otomatik olarak restoran cüzdanlarına transfer edilen ödemelerin özeti. Tüm ödemeler (kredi kartı ve nakit) otomatik olarak işlenir.'}
            {viewType === 'pendingCash' &&
              'Kuryeler tarafından tahsil edilen ancak henüz platforma teslim edilmemiş nakit ödemelerin listesi.'}
            {viewType === 'commissions' &&
              'Platform tarafından siparişlerden alınan komisyon gelirlerinin detaylı listesi.'}
            {viewType === 'payouts' &&
              'Restoran ve kuryelere yapılacak periyodik hakediş ödemelerinin yönetimi. Hakediş oluşturma, onaylama ve CSV dışa aktarma.'}
            {viewType === 'reconciliation' &&
              'Cüzdan bakiyeleri ile ledger kayıtları arasındaki tutarlılığı doğrulayan mutabakat sistemi.'}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
          <Select
            label="Görünüm Türü"
            value={viewType}
            onChange={async (value) => {
              const newViewType = value as 'settlements' | 'pendingCash' | 'commissions';
              setViewType(newViewType);

              if (newViewType === 'pendingCash') {
                setLoadingPendingCash(true);
                try {
                  const data = await adminFinanceService.getPendingCashPayments();
                  setPendingCashPayments(data);
                } catch (error) {
                  console.error('Failed to load pending cash payments:', error);
                  setAlertState({ isOpen: true, title: 'Hata', message: 'Bekleyen nakit ödemeler yüklenemedi', variant: 'danger' });
                } finally {
                  setLoadingPendingCash(false);
                }
              }
            }}
            options={[
              { value: 'settlements', label: 'Restoran Ödemeleri' },
              { value: 'pendingCash', label: 'Bekleyen Nakit Ödemeler' },
              { value: 'commissions', label: 'Komisyonlar' },
              { value: 'payouts', label: 'Hakediş Yönetimi' },
              { value: 'reconciliation', label: 'Mutabakat' },
            ]}
            size="md"
          />

          {viewType === 'settlements' && (
            <>
              <Select
                label="Durum"
                value={settlementFilters.status || 'ALL'}
                onChange={(value) =>
                  setSettlementFilters((prev) => ({
                    ...prev,
                    status: value === 'ALL' ? undefined : (value as any),
                    page: 1,
                  }))
                }
                options={[
                  { value: 'ALL', label: 'Tümü' },
                  { value: 'PROCESSED', label: 'İşlenmiş' },
                ]}
                size="md"
              />

              <DateInput
                label="Başlangıç Tarihi"
                value={settlementFilters.startDate || ''}
                onChange={(e) =>
                  setSettlementFilters((prev) => ({
                    ...prev,
                    startDate: e.target.value || undefined,
                    page: 1,
                  }))
                }
              />

              <DateInput
                label="Bitiş Tarihi"
                value={settlementFilters.endDate || ''}
                onChange={(e) =>
                  setSettlementFilters((prev) => ({
                    ...prev,
                    endDate: e.target.value || undefined,
                    page: 1,
                  }))
                }
              />
            </>
          )}
        </div>

        {viewType === 'reconciliation' && (
          <div className="space-y-4">
            <div className="flex gap-3">
              <Button
                size="sm"
                variant="primary"
                onClick={handleRunReconciliation}
                disabled={reconciliationLoading}
              >
                {reconciliationLoading ? 'Çalışıyor...' : 'Mutabakat Çalıştır'}
              </Button>
            </div>

            {reconciliationResult && (
              <>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <Card>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-gray-900">{reconciliationResult.totalWallets}</p>
                      <p className="text-sm text-gray-600">Toplam Cüzdan</p>
                    </div>
                  </Card>
                  <Card>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-gray-900">{reconciliationResult.checkedWallets}</p>
                      <p className="text-sm text-gray-600">Kontrol Edilen</p>
                    </div>
                  </Card>
                  <Card className={reconciliationResult.mismatches.length > 0 ? 'border-red-300 bg-red-50' : 'border-green-300 bg-green-50'}>
                    <div className="text-center">
                      <p className={`text-2xl font-bold ${reconciliationResult.mismatches.length > 0 ? 'text-red-600' : 'text-green-600'}`}>
                        {reconciliationResult.mismatches.length}
                      </p>
                      <p className="text-sm text-gray-600">Uyumsuzluk</p>
                    </div>
                  </Card>
                  <Card>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-gray-900">{reconciliationResult.orphanedLedgerEntries}</p>
                      <p className="text-sm text-gray-600">Sahipsiz Kayıt</p>
                    </div>
                  </Card>
                </div>

                {reconciliationResult.mismatches.length > 0 && (
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-sm">
                      <thead className="bg-red-50 text-red-800">
                        <tr>
                          <th className="text-left font-semibold px-6 py-3">Cüzdan ID</th>
                          <th className="text-left font-semibold px-6 py-3">Sahip Tipi</th>
                          <th className="text-right font-semibold px-6 py-3">Kayıtlı Bakiye</th>
                          <th className="text-right font-semibold px-6 py-3">Hesaplanan Bakiye</th>
                          <th className="text-right font-semibold px-6 py-3">Fark</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-red-100">
                        {reconciliationResult.mismatches.map((m) => (
                          <tr key={m.walletId} className="hover:bg-red-50/50">
                            <td className="px-6 py-3 font-mono text-xs">{m.walletId.substring(0, 8)}...</td>
                            <td className="px-6 py-3">
                              <Badge variant={m.ownerType === 'RESTAURANT' ? 'info' : m.ownerType === 'COURIER' ? 'warning' : 'secondary'}>
                                {m.ownerType}
                              </Badge>
                            </td>
                            <td className="px-6 py-3 text-right font-mono">{m.recordedBalance}</td>
                            <td className="px-6 py-3 text-right font-mono">{m.computedBalance}</td>
                            <td className="px-6 py-3 text-right font-bold text-red-600 font-mono">{m.difference}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {reconciliationResult.mismatches.length === 0 && (
                  <div className="text-center py-8 bg-green-50 rounded-2xl border border-green-200">
                    <p className="text-green-800 font-semibold text-lg">Tüm cüzdanlar tutarlı</p>
                    <p className="text-green-600 text-sm mt-1">
                      Son kontrol: {new Date(reconciliationResult.runAt).toLocaleString('tr-TR')}
                    </p>
                  </div>
                )}
              </>
            )}

            {!reconciliationResult && !reconciliationLoading && (
              <div className="text-center py-12 text-gray-500">
                Mutabakat sonuçlarını görmek için yukarıdaki butona tıklayın.
              </div>
            )}
          </div>
        )}

        {viewType === 'payouts' && (
          <div className="space-y-4">
            <div className="flex flex-wrap gap-3 items-end">
              <Select
                label="Durum"
                value={payoutStatusFilter}
                onChange={(value) => setPayoutStatusFilter(value as PayoutStatusType | '')}
                options={[
                  { value: '', label: 'Tümü' },
                  { value: 'PENDING', label: 'Beklemede' },
                  { value: 'PROCESSED', label: 'İşlenmiş' },
                  { value: 'FAILED', label: 'Başarısız' },
                ]}
                size="md"
              />
              <Select
                label="Alıcı Tipi"
                value={payoutTypeFilter}
                onChange={(value) => setPayoutTypeFilter(value as PayoutRecipientType | '')}
                options={[
                  { value: '', label: 'Tümü' },
                  { value: 'RESTAURANT', label: 'Restoran' },
                  { value: 'COURIER', label: 'Kurye' },
                ]}
                size="md"
              />
              <Button size="sm" variant="primary" onClick={() => handleGeneratePayouts('restaurant')}>
                Restoran Hakediş Oluştur
              </Button>
              <Button size="sm" variant="primary" onClick={() => handleGeneratePayouts('courier')}>
                Kurye Hakediş Oluştur
              </Button>
              <Button size="sm" variant="secondary" onClick={handleExportCsv}>
                CSV İndir
              </Button>
            </div>

            {payoutsLoading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2" />
                <p className="text-sm text-gray-600">Yükleniyor...</p>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-50 text-gray-600">
                      <tr>
                        <th className="text-left font-semibold px-6 py-3">Alıcı Tipi</th>
                        <th className="text-left font-semibold px-6 py-3">Alıcı ID</th>
                        <th className="text-right font-semibold px-6 py-3">Tutar</th>
                        <th className="text-left font-semibold px-6 py-3">IBAN</th>
                        <th className="text-left font-semibold px-6 py-3">Durum</th>
                        <th className="text-left font-semibold px-6 py-3">Tarih</th>
                        <th className="text-right font-semibold px-6 py-3">İşlemler</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {payouts.length === 0 ? (
                        <tr>
                          <td className="px-6 py-10 text-center text-gray-500" colSpan={7}>
                            Hakediş bulunamadı.
                          </td>
                        </tr>
                      ) : (
                        payouts.map((payout) => (
                          <tr key={payout.id} className="hover:bg-gray-50">
                            <td className="px-6 py-3">
                              <Badge variant={payout.recipientType === 'RESTAURANT' ? 'info' : 'warning'}>
                                {payout.recipientType === 'RESTAURANT' ? 'Restoran' : 'Kurye'}
                              </Badge>
                            </td>
                            <td className="px-6 py-3 text-gray-600 font-mono text-xs">
                              {payout.recipientId.substring(0, 8)}...
                            </td>
                            <td className="px-6 py-3 text-right font-bold text-primary">
                              {formatCurrency(payout.amount)}
                            </td>
                            <td className="px-6 py-3 text-gray-600 font-mono text-xs">
                              {payout.ibanSnapshot}
                            </td>
                            <td className="px-6 py-3">
                              <Badge
                                variant={
                                  payout.status === 'PROCESSED' ? 'success'
                                    : payout.status === 'FAILED' ? 'danger'
                                    : 'warning'
                                }
                              >
                                {payout.status === 'PROCESSED' ? 'İşlenmiş'
                                  : payout.status === 'FAILED' ? 'Başarısız'
                                  : 'Beklemede'}
                              </Badge>
                            </td>
                            <td className="px-6 py-3 text-gray-600">
                              {new Date(payout.createdAt).toLocaleDateString('tr-TR')}
                            </td>
                            <td className="px-6 py-3 text-right">
                              {payout.status === 'PENDING' && (
                                <div className="flex gap-2 justify-end">
                                  <Button
                                    size="sm"
                                    variant="primary"
                                    onClick={() => handleProcessPayout(payout.id)}
                                  >
                                    Onayla
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="danger"
                                    onClick={() => handleFailPayout(payout.id)}
                                  >
                                    Reddet
                                  </Button>
                                </div>
                              )}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                {Math.ceil(payoutsTotal / 20) > 1 && (
                  <div className="flex justify-center gap-2 mt-4">
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => loadPayouts(Math.max(1, payoutsPage - 1))}
                      disabled={payoutsPage === 1}
                    >
                      ← Önceki
                    </Button>
                    <span className="px-4 py-2 text-gray-700">
                      Sayfa {payoutsPage} / {Math.ceil(payoutsTotal / 20)}
                    </span>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => loadPayouts(Math.min(Math.ceil(payoutsTotal / 20), payoutsPage + 1))}
                      disabled={payoutsPage === Math.ceil(payoutsTotal / 20)}
                    >
                      Sonraki →
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {viewType === 'pendingCash' && (
          <PendingCashPaymentsList
            payments={pendingCashPayments}
            isLoading={loadingPendingCash}
          />
        )}

        {viewType === 'commissions' && (
          <CommissionsList commissions={commissions.data} isLoading={commissions.isLoading} />
        )}

        {viewType === 'settlements' && (
          <SettlementsList
            settlements={settlements.data?.settlements || []}
            isLoading={settlements.isLoading}
            onViewDetails={handleViewSettlement}
            totalPages={totalPages}
            currentPage={settlementFilters.page || 1}
            onPageChange={(page) => setSettlementFilters((prev) => ({ ...prev, page }))}
          />
        )}
      </Card>

      {isSettlementModalOpen && selectedSettlement && (
        <SettlementDetailModal
          settlement={selectedSettlement}
          onClose={() => {
            setIsSettlementModalOpen(false);
            setSelectedSettlement(null);
          }}
          onApprove={() => {}}
          onReject={() => {}}
        />
      )}

      <AlertModal
        isOpen={alertState.isOpen}
        title={alertState.title}
        message={alertState.message}
        variant={alertState.variant}
        onClose={() => setAlertState((a) => ({ ...a, isOpen: false }))}
      />

      {confirmState.isOpen && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[9999] p-4">
          <Card className="max-w-md w-full shadow-2xl">
            <div className="p-6 space-y-4">
              <h3 className="text-lg font-bold text-gray-900">{confirmState.title}</h3>
              <p className="text-gray-600 whitespace-pre-line">{confirmState.message}</p>
              <div className="flex gap-3 justify-end">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setConfirmState((c) => ({ ...c, isOpen: false }))}
                >
                  İptal
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={confirmState.onConfirm}
                >
                  Onayla
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}

interface SettlementsListProps {
  settlements: Settlement[];
  isLoading: boolean;
  onViewDetails: (id: string) => void;
  totalPages: number;
  currentPage: number;
  onPageChange: (page: number) => void;
}

function SettlementsList({
  settlements,
  isLoading,
  onViewDetails,
  totalPages,
  currentPage,
  onPageChange,
}: SettlementsListProps) {
  if (isLoading) {
    return (
      <div className="text-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2" />
        <p className="text-sm text-gray-600">Yükleniyor...</p>
      </div>
    );
  }

  return (
    <>
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 text-gray-600">
            <tr>
              <th className="text-left font-semibold px-6 py-3">Restoran</th>
              <th className="text-right font-semibold px-6 py-3">Toplam Tutar</th>
              <th className="text-right font-semibold px-6 py-3">Sipariş Sayısı</th>
              <th className="text-left font-semibold px-6 py-3">Onay Durumu</th>
              <th className="text-right font-semibold px-6 py-3">İşlemler</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {settlements.length === 0 ? (
              <tr>
                <td className="px-6 py-10 text-center text-gray-500" colSpan={5}>
                  Ödeme bulunamadı.
                </td>
              </tr>
            ) : (
              settlements.map((settlement) => (
                <tr key={settlement.id} className="hover:bg-gray-50">
                  <td className="px-6 py-3 text-gray-900 font-medium">
                    {settlement.restaurantName}
                  </td>
                  <td className="px-6 py-3 text-right font-bold text-primary">
                    {formatCurrency(settlement.amount)}
                  </td>
                  <td className="px-6 py-3 text-right text-gray-600">{settlement.orderCount}</td>
                  <td className="px-6 py-3">
                    <Badge
                      variant={
                        settlement.status === 'APPROVED'
                          ? 'success'
                          : settlement.status === 'REJECTED'
                          ? 'danger'
                          : settlement.status === 'PROCESSED'
                          ? 'success'
                          : 'warning'
                      }
                    >
                      {settlement.status === 'PROCESSED'
                        ? 'İşlenmiş'
                        : settlement.status === 'APPROVED'
                        ? 'Onaylanmış'
                        : settlement.status === 'REJECTED'
                        ? 'Reddedilmiş'
                        : settlement.status === 'PENDING'
                        ? 'Beklemede'
                        : settlement.status}
                    </Badge>
                  </td>
                  <td className="px-6 py-3 text-right">
                    <Button size="sm" variant="primary" onClick={() => onViewDetails(settlement.id)}>
                      Detay
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="flex justify-center gap-2 mt-4">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => onPageChange(Math.max(1, currentPage - 1))}
            disabled={currentPage === 1}
          >
            ← Önceki
          </Button>
          <span className="px-4 py-2 text-gray-700">
            Sayfa {currentPage} / {totalPages}
          </span>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
            disabled={currentPage === totalPages}
          >
            Sonraki →
          </Button>
        </div>
      )}
    </>
  );
}

interface CommissionsListProps {
  commissions: any[];
  isLoading: boolean;
}

function CommissionsList({ commissions, isLoading }: CommissionsListProps) {
  if (isLoading) {
    return (
      <div className="text-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2" />
        <p className="text-sm text-gray-600">Yükleniyor...</p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-sm">
        <thead className="bg-gray-50 text-gray-600">
          <tr>
            <th className="text-left font-semibold px-6 py-3">Restoran</th>
            <th className="text-right font-semibold px-6 py-3">Komisyon</th>
            <th className="text-left font-semibold px-6 py-3">Tarih</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {commissions.length === 0 ? (
            <tr>
              <td className="px-6 py-10 text-center text-gray-500" colSpan={3}>
                Komisyon bulunamadı.
              </td>
            </tr>
          ) : (
            commissions.map((commission) => (
              <tr key={commission.id} className="hover:bg-gray-50">
                <td className="px-6 py-3 text-gray-900">
                  {commission.restaurantName || '—'}
                </td>
                <td className="px-6 py-3 text-right font-bold text-primary">
                  {formatCurrency(commission.amount)}
                </td>
                <td className="px-6 py-3 text-gray-600">
                  {new Date(commission.createdAt).toLocaleDateString('tr-TR')}
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

interface SettlementDetailModalProps {
  settlement: Settlement;
  onClose: () => void;
  onApprove?: (notes?: string) => void;
  onReject?: (reason: string) => void;
}

function SettlementDetailModal({
  settlement,
  onClose,
  onApprove,
  onReject,
}: SettlementDetailModalProps) {

  const periodStart = settlement.periodStart
    ? new Date(settlement.periodStart).toLocaleDateString('tr-TR', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      })
    : '—';
  const periodEnd = settlement.periodEnd
    ? new Date(settlement.periodEnd).toLocaleDateString('tr-TR', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      })
    : '—';

  const statusLabels = {
    PENDING: 'Onay Bekliyor',
    APPROVED: 'Onaylanmış',
    REJECTED: 'Reddedilmiş',
    PROCESSED: 'İşlenmiş',
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[9999] p-4">
      <Card className="max-w-5xl w-full max-h-[95vh] overflow-hidden flex flex-col shadow-2xl">
        <div className="bg-gradient-to-r from-[#E63946] to-[#C1121F] text-white p-6 rounded-t-2xl">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-2xl font-bold mb-1">Ödeme Özeti: {settlement.restaurantName}</h3>
              <p className="text-white/90 text-sm">
                Dönem: {periodStart} - {periodEnd}
              </p>
            </div>
            <button
              onClick={onClose}
              className="w-10 h-10 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors"
            >
              <span className="text-xl">×</span>
            </button>
          </div>
        </div>

        <div className="overflow-y-auto flex-1 p-6 bg-[#F9FAFB]">
          <div className="space-y-6">
            <div className="bg-blue-50 border border-blue-200 rounded-2xl p-4">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                  <span className="text-lg">ℹ️</span>
                </div>
                <div>
                  <p className="font-semibold text-blue-900 mb-1">Otomatik Ödeme Sistemi</p>
                  <p className="text-sm text-blue-800">
                    Bu ödeme özeti, siparişler teslim edildiğinde otomatik olarak restoran cüzdanına transfer edilen
                    ödemelerin toplamını gösterir. Tüm ödemeler (kredi kartı ve nakit) otomatik olarak işlenir ve para
                    zaten restoran cüzdanına aktarılmış durumdadır. Nakit ödemelerde kurye parayı alır ve platforma
                    borçlu olur, ancak restoran ödemesi yine de otomatik olarak yapılır.
                  </p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-xl bg-green-50 flex items-center justify-center">
                    <span className="text-xl">💰</span>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wide">Toplam Tutar</p>
                    <p className="text-xl font-bold text-[#E63946] mt-1">{formatCurrency(settlement.amount)}</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center">
                    <span className="text-xl">📦</span>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wide">Sipariş Sayısı</p>
                    <p className="text-xl font-bold text-gray-900 mt-1">{settlement.orderCount}</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-xl bg-green-50 flex items-center justify-center">
                    <span className="text-xl">✓</span>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wide">Durum</p>
                    <Badge variant="success" className="mt-1">
                      {statusLabels[settlement.status as keyof typeof statusLabels] || 'İşlenmiş'}
                    </Badge>
                  </div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-8 h-8 rounded-lg bg-[#2A9D8F]/10 flex items-center justify-center">
                    <span className="text-lg">📅</span>
                  </div>
                  <h4 className="font-bold text-gray-900 text-lg">Dönem Bilgisi</h4>
                </div>
                <div className="space-y-3">
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Başlangıç Tarihi</p>
                    <p className="text-gray-900 font-medium">{periodStart}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Bitiş Tarihi</p>
                    <p className="text-gray-900 font-medium">{periodEnd}</p>
                  </div>
                  {settlement.createdAt && (
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Oluşturulma Tarihi</p>
                      <p className="text-gray-900 font-medium">
                        {new Date(settlement.createdAt).toLocaleDateString('tr-TR', {
                          day: 'numeric',
                          month: 'long',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-8 h-8 rounded-lg bg-orange-50 flex items-center justify-center">
                    <span className="text-lg">🍽️</span>
                  </div>
                  <h4 className="font-bold text-gray-900 text-lg">Restoran Bilgisi</h4>
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-1">Restoran Adı</p>
                  <p className="text-gray-900 font-medium">{settlement.restaurantName}</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center">
                  <span className="text-lg">💳</span>
                </div>
                <h4 className="font-bold text-gray-900 text-lg">Ödeme Türü Dağılımı</h4>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-green-50 rounded-xl p-4 border border-green-200">
                  <p className="text-xs text-green-700 mb-1">Kredi Kartı</p>
                  <p className="text-lg font-bold text-green-900">
                    {settlement.creditCardOrderCount ?? 0} sipariş
                  </p>
                </div>
                <div className="bg-orange-50 rounded-xl p-4 border border-orange-200">
                  <p className="text-xs text-orange-700 mb-1">Nakit (Kapıda Ödeme)</p>
                  <p className="text-lg font-bold text-orange-900">{settlement.cashOrderCount ?? 0} sipariş</p>
                  <p className="text-xs text-orange-600 mt-1">
                    Kurye parayı alır, restoran ödemesi otomatik yapılır
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
}

interface PendingCashPaymentsListProps {
  payments: {
    orders: Array<{
      orderId: string;
      orderNumber: string;
      totalAmount: number;
      courierId: string;
      courierName: string;
      courierPhone: string;
      debtAmount: string;
      createdAt: Date;
      deliveredAt: Date | null;
      daysSinceDelivery: number;
    }>;
    totalPendingAmount: string;
  } | null;
  isLoading: boolean;
}

function PendingCashPaymentsList({ payments, isLoading }: PendingCashPaymentsListProps) {
  if (isLoading) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-500">Yükleniyor...</p>
      </div>
    );
  }

  if (!payments || payments.orders.length === 0) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-500">Bekleyen nakit ödeme bulunamadı.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="bg-blue-50 border border-blue-200 rounded-2xl p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="font-semibold text-blue-900">Toplam Bekleyen Tutar</p>
            <p className="text-2xl font-bold text-blue-900 mt-1">
              {formatCurrency(parseFloat(payments.totalPendingAmount))}
            </p>
          </div>
          <div className="text-right">
            <p className="text-sm text-blue-700">Toplam Sipariş</p>
            <p className="text-xl font-bold text-blue-900 mt-1">{payments.orders.length}</p>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="text-left font-semibold px-6 py-3">Sipariş No</th>
              <th className="text-left font-semibold px-6 py-3">Kurye</th>
              <th className="text-right font-semibold px-6 py-3">Bekleyen Tutar</th>
              <th className="text-right font-semibold px-6 py-3">Teslim Tarihi</th>
              <th className="text-right font-semibold px-6 py-3">Gün Sayısı</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {payments.orders.map((order) => (
              <tr key={order.orderId} className="hover:bg-gray-50">
                <td className="px-6 py-3">
                  <span className="font-medium text-gray-900">{order.orderNumber}</span>
                </td>
                <td className="px-6 py-3">
                  <div>
                    <p className="text-gray-900 font-medium">{order.courierName}</p>
                    <p className="text-sm text-gray-500">{order.courierPhone}</p>
                  </div>
                </td>
                <td className="px-6 py-3 text-right">
                  <span className="font-bold text-[#E63946]">
                    {formatCurrency(parseFloat(order.debtAmount))}
                  </span>
                </td>
                <td className="px-6 py-3 text-right text-gray-600">
                  {order.deliveredAt
                    ? new Date(order.deliveredAt).toLocaleDateString('tr-TR', {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric',
                      })
                    : '—'}
                </td>
                <td className="px-6 py-3 text-right">
                  <Badge
                    variant={order.daysSinceDelivery > 7 ? 'danger' : order.daysSinceDelivery > 3 ? 'warning' : 'success'}
                  >
                    {order.daysSinceDelivery} gün
                  </Badge>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
